import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import type {
  ReportReviewMatrixData,
  ReportReviewSettingsTarget,
} from "../../../services/admin/reportReviewSettings.service";
import { ReportReviewGlobalActions } from "./ReportReviewGlobalActions";
import { ReportReviewMatrixHeader } from "./ReportReviewMatrixHeader";
import { ReportReviewPrefixRow } from "./ReportReviewPrefixRow";

interface PendingChange {
  target: ReportReviewSettingsTarget;
  reviewRequired: boolean;
}

interface ReportReviewMatrixProps {
  matrix: ReportReviewMatrixData;
  disabled: boolean;
  onUpdate: (change: PendingChange) => void;
}

export function ReportReviewMatrix({
  matrix,
  disabled,
  onUpdate,
}: ReportReviewMatrixProps) {
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(
    null
  );
  const optionalReviewKeys = useMemo(
    () =>
      new Set(
        matrix.relaxedCombinations.map(
          (row) => `${row.prefixId}:${row.pathologistUserId}`
        )
      ),
    [matrix.relaxedCombinations]
  );
  const requestChange = useCallback(
    (change: PendingChange) => {
      if (change.reviewRequired) onUpdate(change);
      else setPendingChange(change);
    },
    [onUpdate]
  );
  const handleCellChange = useCallback(
    (prefixId: string, pathologistUserId: string, reviewRequired: boolean) => {
      requestChange({
        target: { type: "combination", prefixId, pathologistUserId },
        reviewRequired,
      });
    },
    [requestChange]
  );
  const handlePrefixChange = useCallback(
    (prefixId: string, reviewRequired: boolean) => {
      requestChange({ target: { type: "prefix", prefixId }, reviewRequired });
    },
    [requestChange]
  );
  const handlePathologistChange = useCallback(
    (pathologistUserId: string, reviewRequired: boolean) => {
      requestChange({
        target: { type: "pathologist", pathologistUserId },
        reviewRequired,
      });
    },
    [requestChange]
  );
  const handleGlobalChange = useCallback(
    (reviewRequired: boolean) => {
      requestChange({ target: { type: "global" }, reviewRequired });
    },
    [requestChange]
  );
  const handleCancel = useCallback(() => setPendingChange(null), []);
  const handleConfirm = useCallback(() => {
    if (pendingChange) onUpdate(pendingChange);
    setPendingChange(null);
  }, [onUpdate, pendingChange]);

  return (
    <Stack>
      <Text size="sm" c="dimmed">
        Checked means report review is required.
      </Text>
      <ReportReviewGlobalActions
        disabled={disabled}
        onChange={handleGlobalChange}
      />
      <ScrollArea type="auto" offsetScrollbars>
        <Table striped withTableBorder withColumnBorders>
          <ReportReviewMatrixHeader
            pathologists={matrix.pathologists}
            disabled={disabled}
            onPathologistChange={handlePathologistChange}
          />
          <Table.Tbody>
            {matrix.prefixes.map((prefix) => (
              <ReportReviewPrefixRow
                key={prefix.id}
                prefix={prefix}
                pathologists={matrix.pathologists}
                optionalReviewKeys={optionalReviewKeys}
                disabled={disabled}
                onCellChange={handleCellChange}
                onPrefixChange={handlePrefixChange}
              />
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      <Modal
        opened={Boolean(pendingChange)}
        onClose={handleCancel}
        title="Make report preview optional?"
        centered
      >
        <Stack>
          <Text>
            Matching sign-outs will allow the pathologist to sign out without
            previewing the rendered report.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCancel} data-autofocus>
              Cancel
            </Button>
            <Button color="red" onClick={handleConfirm}>
              Make preview optional
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
